import { createRoute, redirect } from "@tanstack/react-router";
import { isMongoId } from "validator";
import { z } from "zod";

import { Workflows } from "./view";

import { getFolderBreadcrumbs } from "../../services/folders";
import { getRecentReviews } from "../../services/reviews";
import {
  canAccessCreativeConnect,
  canAccessEditOwnProfile,
  canAccessLists,
  canAccessPuntt,
  canAccessPunttDashboard,
  requiresAuth,
} from "../../utils/auth";
import { createDefaultNetworkRouteSearch } from "../../utils/constants";
import { mergePunttProjectState } from "../../utils/slices/punttProjects";
import { store } from "../../utils/store";
import { authLayoutRoute } from "../auth-layout/route";
import { listsRoute } from "../lists/route";
import { myNetworkRoute } from "../network/route";
import { ticketsRoute } from "../tickets/route";
import { aboutMeEditRoute } from "../userProfile/routes/AboutMe/editRoute";

type WorkflowSearchParams = {
  folderId?: string;
  participants?: string[];
  ticketTitle?: string;
  pageSize?: number;
};

export const workflowsRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: "workflows",
  validateSearch(rawSearchParams): WorkflowSearchParams {
    const searchParser = z.object({
      folderId: z
        .string()
        .optional()
        .refine((val) => (val != null ? isMongoId(val) : true)),
      // preprocess is nice to remove empty arrays from the URL bar, but
      // `validateSearch` is not executed on every route invalidation
      participants: z.preprocess((arg) => {
        if (Array.isArray(arg) && arg.length === 0) {
          return undefined;
        }

        return arg;
      }, z.array(z.string()).optional()),
      ticketTitle: z.preprocess((arg) => {
        if (arg == null || arg === "") {
          return undefined;
        }

        return arg;
      }, z.string().optional()),
      pageSize: z.coerce.number().optional(),
    });

    return searchParser.parse(rawSearchParams);
  },
  beforeLoad({ search }) {
    requiresAuth();

    if (search.redirect) {
      throw redirect({ to: search.redirect });
    }

    if (!canAccessPunttDashboard()) {
      if (canAccessPuntt()) {
        throw redirect({ to: ticketsRoute.to });
      }

      if (canAccessCreativeConnect()) {
        throw redirect({
          to: myNetworkRoute.to,
          search: createDefaultNetworkRouteSearch(),
        });
      }

      if (canAccessLists()) {
        throw redirect({ to: listsRoute.to });
      }

      if (canAccessEditOwnProfile()) {
        throw redirect({ to: aboutMeEditRoute.to });
      }
    }
  },
  loaderDeps({ search }) {
    const { folderId, participants, ticketTitle, pageSize } =
      search as WorkflowSearchParams;

    return {
      folderId,
      participants,
      ticketTitle,
      pageSize,
    };
  },
  async loader({ context, deps }) {
    const { value: user } = store.getState().auth;

    if (user?.ticketId) return { recentWorkflows: [] };

    const { queryClient } = context;
    const { folderId, ticketTitle, pageSize } = deps;

    const recentWorkflows = await queryClient.fetchQuery({
      queryKey: ["recent-workflow", folderId, ticketTitle, pageSize],
      queryFn: () =>
        getRecentReviews({
          folderId,
          query: ticketTitle,
          limit: pageSize?.toString(),
        }),
    });

    const breadcrumbsJson = await queryClient.fetchQuery({
      queryKey: ["breadcrumbs", folderId],
      queryFn: () => getFolderBreadcrumbs(folderId),
    });

    store.dispatch(
      mergePunttProjectState({
        breadcrumbs: breadcrumbsJson,
      }),
    );

    return {
      recentWorkflows,
    };
  },
  component: Workflows,
});
